import React, { Fragment, useEffect, useState } from 'react';
import bemCn from 'bem-cn';
import loadable from '@loadable/component';
import { Helmet } from 'react-helmet';
import CookieService from '../../services/cookie.service';
import { BLOG_TYPES } from '../../consts/blog-system.consts';
import { IMG_RESIZES } from '../../consts/resizer.consts';
import { ImageHelper } from '../../classes/image-helper.class';
import useMobileScreen from '../../services/hooks/useMobileScreen';
import { numFormatter } from '../../helpers/price';
import { PREVIEW_TYPES } from '../../consts/images.const';
import { RouteFormatter } from '../../routes';
import { capitalizeText } from '../../helpers/text';
import { smartBannerShow } from '../../utils';
import { COOKIE_PARAM_NAME } from '../../consts/cookies.consts';
import { BREEDS, DEFAULT_FAQS, GENDERS, SORT_BY, STATES } from '../../consts/blog-dog-names.consts';
import BlogArticleDesktopLow from '../../components/blog-article-desktop-low';
import { BlogAuthorDetails } from '../../components/blogs/author-details';
import { Banner1, Banner2 } from '../../components/blogs/banners';
import SniffCarouselClient from '../../components/carousel';
import BlogArticleSimple from '../../components/blog-article-simple';
import { BlogSubDetail } from '../../components/blogs/sub-details';
import SniffCarouselSSR from '../../components/carousel/carousel-ssr';
import SpotBreadcrumbs from '../../components/spot-breadcrumbs';
import BlogCategories from '../../components/blog-categories';
import BlogTopSticky from '../../components/blog-top-nav/top-sticky';
import MobileSubPage from '../../components/mobile-sub-page';
import BlogSideBar from '../../components/blog-side-bar';
import SniffButton from '../../components/sniff-button';
import SniffSelect from '../../components/sniff-select';
import ModalMobile from '../../components/modal-mobile';
import BlogTopNav from '../../components/blog-top-nav';
import BlogFooter from '../../components/blog-footer';
import PictureSet from '../../components/picture-set';
import SharePage from '../../components/spot-share-button/share-page';
import Preloader from '../../components/preloader';
import SnifHr from '../../components/snif-hr';
import SvgChevronRight from 'src/assets/svg/SvgChevronRight.svg';
import SvgChevronDown from 'src/assets/svg/SvgChevronDown.svg';
import SvgChevronLeft from 'src/assets/svg/SvgChevronLeft.svg';
import SvgSearch from 'src/assets/svg/SvgSearch.svg';
import SvgShare from 'src/assets/svg/SvgShare.svg';
import emptyImg from '../../assets/img/empty-spot.png';
import '../blog/post.scss';
import './style.scss';

const FooterNew = loadable(() => import('../../components/footer-new'));
const DesktopPhotoGallery = loadable(() => import('../spot-view-page/desktop-photo-gallery'));

const useHistory = require('../../services/hooks/useHistory');
const useGetDogNamesC = require('../../services/hooks/useGetDogNames');
const useGetDogNamesPage = require('../../services/hooks/useGetDogNamesPage');
const useGetBlogCategories = require('../../services/hooks/useGetBlogCategories');
const useGetDogNamesProfile = require('../../services/hooks/useGetDogNamesProfile');

const NAME_PER_PAGE = 50;

const b = bemCn('sniff-blog-post');
const bDog = bemCn('sniff-dog-name');

const endsWithDog = (str) => /dog$/i.test(str);

const generateSlug = (selGender, selBreed, selState) => {
    const urlBuilder = [];
    selGender.value && urlBuilder.push(selGender.value);
    selBreed.value && urlBuilder.push(selBreed.value);
    selState.value && urlBuilder.push(selState.value);
    const filteredUrl = urlBuilder.join('-');
    return filteredUrl;
};

const generateTitles = ({ defGender, defBreed, defState, chosenGender, chosenBreed, chosenState }) => {
    let mainTitle = 'Top 1,000 Most Popular Dog and Puppy Names';
    let faqTitle = 'Frequently Asked Questions When Naming A Dog';
    let genderTitle = 'Most Popular Dog Names By Gender';
    let breedTitle = 'Most Popular Dog Names By Breed';
    let stateTitle = 'Most Popular Dog Names By State';
    const breedNames = `${chosenBreed.label} ${endsWithDog(chosenBreed.label) ? '' : 'Dog '}Names`;

    if (defGender?.slug && defBreed?.slug && defState?.slug) {
        // gender-breed-state
        mainTitle = `Most Popular ${chosenGender.label} ${breedNames} In ${chosenState.label}`;
        faqTitle = `${chosenState.label} Dog Owners’ Frequently Asked Questions When Naming Their ${chosenGender.label} ${chosenBreed.label}`;
        breedTitle = `See The Most Popular ${chosenGender.label} Names Across All Breeds In ${chosenState.label}`;
        stateTitle = `See The Most Popular ${chosenGender.label} ${breedNames} In Other States Near ${chosenState.label}`;
    } else if (defGender?.slug && defBreed?.slug && !defState?.slug) {
        // gender-breed
        mainTitle = `Most Popular ${chosenGender.label} ${breedNames}`;
        faqTitle = `Dog Owners’ Frequently Asked Questions When Naming Their ${chosenGender.label} ${chosenBreed.label}`;
        breedTitle = `See The Most Popular ${chosenGender.label} Dog Names Across All Breeds`;
        stateTitle = `See The Most Popular ${chosenGender.label} ${breedNames} By State`;
    } else if (defGender?.slug && !defBreed?.slug && defState?.slug) {
        // gender-state
        mainTitle = `Most Popular ${chosenGender.label} Dog Names In ${chosenState.label}`;
        faqTitle = `${chosenState.label} Dog Owners’ Frequently Asked Questions When Naming Their ${chosenGender.label} Dog`;
        breedTitle = `See The Most Popular ${chosenGender.label} Dog Names Across All Breeds In ${chosenState.label}`;
        stateTitle = `See The Most Popular ${chosenGender.label} Dog Names In Other States Near ${chosenState.label}`;
    } else if (!defGender?.slug && defBreed?.slug && defState?.slug) {
        // breed-state
        mainTitle = `Most Popular ${breedNames} In ${chosenState.label}`;
        genderTitle = `See The Most Popular Dog Names By Gender In ${chosenState.label}`;
        faqTitle = `${chosenState.label} Dog Owners’ Frequently Asked Questions When Naming Their ${chosenBreed.label}`;
        breedTitle = `See The Most Popular Dog Names Across All Breeds In ${chosenState.label}`;
        stateTitle = `See The Most Popular ${breedNames} In Other States Near ${chosenState.label}`;
    } else if (defGender?.slug && !defBreed?.slug && !defState?.slug) {
        // gender
        mainTitle = `Most Popular ${chosenGender.label} Dog Names`;
        faqTitle = `Dog Owners’ Frequently Asked Questions When Naming Their ${chosenGender.label} Dog`;
        breedTitle = `See The Most Popular ${chosenGender.label} Dog Names Across All Breeds`;
        stateTitle = `See The Most Popular ${chosenGender.label} Dog Names By State`;
    } else if (!defGender?.slug && defBreed?.slug && !defState?.slug) {
        // breed
        mainTitle = `Most Popular ${breedNames}`;
        genderTitle = `See The Most Popular ${breedNames} By Gender`;
        breedTitle = 'See The Most Popular Dog Names Across All Breeds';
        faqTitle = `Dog Owners’ Frequently Asked Questions When Naming Their ${chosenBreed.label}`;
        stateTitle = `See The Most Popular ${breedNames} By State`;
    } else if (!defGender?.slug && !defBreed?.slug && defState?.slug) {
        // state
        mainTitle = `Most Popular Dog Names in ${chosenState.label}`;
        faqTitle = `${chosenState.label} Dog Owners’ Frequently Asked Questions When Naming Their Dog`;
        breedTitle = `See The Most Popular Dog Names Across All Breeds In ${chosenState.label}`;
        stateTitle = `See The Most Popular Dog Names In Other States Near ${chosenState.label}`;
    }

    return { mainTitle, faqTitle, genderTitle, breedTitle, stateTitle };
};

const TriggerComp = () => (
    <SniffButton size="lg" color="secondary" className={bDog('share-btn')}>
        <SvgShare />
        <span className="d-none d-md-block ms-1">Share</span>
    </SniffButton>
);

const NoResultTbl = ({ handleResetFilters }) => (
    <div className={bDog('empty-table')}>
        <SvgSearch className="mb-3" width={32} height={32} />
        <p className="snif-semibold mb-1">No results found</p>
        <p className="mb-3">Please try searching with a different search query.</p>
        <SniffButton color="secondary" onClick={handleResetFilters}>
            Reset filters
        </SniffButton>
    </div>
);

const DogNameTableMobile = ({ handleResetFilters, lastPage, loadMoreDogNames, sortedDogNames = [], show }) => {
    const hasDogNames = sortedDogNames.length > 0;
    const loadMore = () => loadMoreDogNames();
    const goToProfile = (e, slug) => {
        if (!slug) return;
        e.preventDefault();
        window.location = slug;
    };

    return (
        <>
            <div className={bDog('dog-name-tbl-mobile').mix(show ? 'f-show' : '')}>
                {hasDogNames ? (
                    sortedDogNames.map((d) => {
                        return (
                            <div key={d.id}>
                                <table>
                                    <tbody>
                                        <tr>
                                            <th>RANK:</th>
                                            <td>#{d.rank}</td>
                                        </tr>
                                        <tr>
                                            <th>NAME:</th>
                                            <td onClick={(e) => goToProfile(e, d?.slug)}>
                                                {d?.slug && d?.about ? (
                                                    <a href={d.slug} rel={d.noFollow ? 'nofollow' : ''} className="text-primary text-underline">
                                                        <p className="snif-bold">{d.dogname}</p>
                                                    </a>
                                                ) : (
                                                    <p className="snif-bold">{d.dogname}</p>
                                                )}
                                                {d?.slug && d?.about && <p>{d.about}</p>}
                                            </td>
                                        </tr>
                                        <tr>
                                            <th>TOP GENDER:</th>
                                            <td className="text-first-cap">{d.topGender?.toLowerCase()}</td>
                                        </tr>
                                        <tr>
                                            <th>TOP BREED:</th>
                                            <td>{d.topBreed}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        );
                    })
                ) : (
                    <NoResultTbl {...{ handleResetFilters }} />
                )}
            </div>
            {!lastPage && (
                <SniffButton size="lg" color="secondary" className="d-md-none mt-2" block onClick={loadMore}>
                    Load more names
                </SniffButton>
            )}
        </>
    );
};

const DogNameTableDesktop = ({ handleResetFilters, lastPage, loadMoreDogNames, sortedDogNames = [] }) => {
    const hasDogNames = sortedDogNames.length > 0;
    const loadMore = () => loadMoreDogNames();
    const goToProfile = (e, slug) => {
        if (!slug) return;
        e.preventDefault();
        window.location = slug;
    };

    return (
        <>
            <table className={bDog('dog-name-tbl')}>
                <thead>
                    <tr>
                        <th>Rank</th>
                        <th>Name and About</th>
                        <th>Top gender</th>
                        <th>Top breed</th>
                    </tr>
                </thead>
                <tbody>
                    {hasDogNames ? (
                        sortedDogNames.map((d) => {
                            return (
                                <tr key={d.id}>
                                    <td>#{d.rank}</td>
                                    <td onClick={(e) => goToProfile(e, d?.slug)}>
                                        {d?.slug ? (
                                            <a href={d.slug} rel={d.noFollow ? 'nofollow' : ''} className="text-primary text-underline">
                                                <p className="snif-semibold">{d.dogname}</p>
                                            </a>
                                        ) : (
                                            <p className="snif-semibold">{d.dogname}</p>
                                        )}
                                        {d?.slug && d?.about && <p>{d.about}</p>}
                                    </td>
                                    <td className="text-capitalize">{d.topGender?.toLowerCase()}</td>
                                    <td className="text-capitalize">{d.topBreed}</td>
                                </tr>
                            );
                        })
                    ) : (
                        <tr>
                            <td colSpan={5}>
                                <NoResultTbl {...{ handleResetFilters }} />
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
            {!lastPage && (
                <SniffButton size="lg" color="secondary" className="d-none d-md-inline-block mt-3" onClick={loadMore}>
                    Load more names
                </SniffButton>
            )}
        </>
    );
};

const RenderMobileFilters = ({ selBreed, selGender, selState, toggleBreedModal, toggleGenderModal, toggleStateModal }) => (
    <>
        <div className={bDog('filters_sel')} onClick={toggleGenderModal}>
            {selGender.label}
            <SvgChevronDown />
        </div>
        <div className={bDog('filters_sel')} onClick={toggleBreedModal}>
            {selBreed.label}
            <SvgChevronDown />
        </div>
        <div className={bDog('filters_sel')} onClick={toggleStateModal}>
            {selState.label}
            <SvgChevronDown />
        </div>
    </>
);

const RenderFilters = ({ handleApplyFilters, GENDERS, selGender, setSelGender, selBreed, setSelBreed, selState, setSelState, TOP_BREEDS }) => (
    <>
        <SniffSelect options={GENDERS} value={selGender} onChange={setSelGender} />
        <SniffSelect options={TOP_BREEDS} value={selBreed} onChange={setSelBreed} />
        <SniffSelect options={STATES} value={selState} onChange={setSelState} />
        <SniffButton size="lg" onClick={handleApplyFilters}>
            Apply filters
        </SniffButton>
    </>
);

const RenderSortBy = ({ selSortBy, setSelSortBy }) => <SniffSelect options={SORT_BY} value={selSortBy} onChange={setSelSortBy} />;

const RenderMainAbout = ({ blogPost, defBreed, defGender, defState }) => {
    if (blogPost?.about) {
        return blogPost.about;
    }

    if (!defBreed?.slug && !defGender?.slug && !defState?.slug) {
        return (
            <>
                Looking for the perfect dog name for your new pup? We have created filterable lists of dog names from our database of hundreds of
                thousands of{' '}
                <a href={RouteFormatter.home()} className="snif-medium text-underline text-primary">
                    Sniffspot
                </a>{' '}
                users. You can filter by gender, breed and state to find the most cute, unique and creative dog names.
            </>
        );
    }

    return (
        <>
            Looking for the perfect dog name for your new pup? We have created filterable lists of dog names from our database of hundreds of
            thousands of{' '}
            <a href={RouteFormatter.home()} className="snif-medium text-underline text-primary">
                Sniffspot
            </a>{' '}
            users. You can filter by gender, breed and state to find the most cute, unique and creative dog names.
        </>
    );
};

const RenderSeoContent = ({ blogPost, blogCategories, handleGo, previousPost, nextPost, recentPosts, useStateSSR }) => (
    <div className="desktop-container">
        <div className={b('prev-next-post')}>
            {previousPost && (
                <div className={b('prev-next-post_prev')} onClick={(e) => handleGo(e, previousPost.slug)}>
                    <SvgChevronLeft />
                    <div>
                        <p className="snif-xs snif-bold text-primary mb-1 pb-05 mb-md-2 pb-md-0">
                            <a href={previousPost.slug}>PREVIOUS POST</a>
                        </p>
                        <BlogArticleSimple {...{ data: previousPost, mobileSquare: true, useStateSSR }} />
                    </div>
                </div>
            )}
            {nextPost && (
                <div className={b('prev-next-post_next')} onClick={(e) => handleGo(e, nextPost.slug)}>
                    <div>
                        <p className="snif-xs snif-bold text-primary mb-1 pb-05 mb-md-2 pb-md-0">
                            <a href={nextPost.slug}>NEXT POST</a>
                        </p>
                        <BlogArticleSimple {...{ data: nextPost, mobileSquare: true, useStateSSR }} />
                    </div>
                    <SvgChevronRight />
                </div>
            )}
        </div>
        <div className="mx-2 mx-md-0">
            <h2 className="snif-m2 snif-l2-pc snif-semibold snif-bold-pc mb-3 mb-md-5">Most recent articles</h2>
            <div className={b('recent')}>
                {recentPosts.map((v) => (
                    <Fragment key={v.id}>
                        <BlogArticleSimple data={v} {...{ useStateSSR }} />
                        <BlogArticleDesktopLow data={v} {...{ useStateSSR }} />
                    </Fragment>
                ))}
            </div>
            <div className="d-md-none">
                <h5 className="snif-m2 snif-semibold mb-3">Related articles</h5>
                {blogPost?.relatedPosts?.map((v) => (
                    <BlogArticleSimple key={v.id} data={v} {...{ useStateSSR }} />
                ))}
            </div>
            <h2 className="snif-m2 snif-l2-pc snif-semibold snif-bold-pc mb-3 mb-md-5 pt-3 pt-md-0">Top dog guides per area</h2>
            <BlogCategories categories={blogCategories} />
        </div>
    </div>
);

const RenderFaqs = ({ customFaqs, faqTitle, faqContent }) => (
    <>
        <h2 id="FAQS" className="snif-m1 snif-l2-pc snif-semibold text-capitalize mb-2">
            {faqTitle}
        </h2>
        <div className={bDog('faqs-listings-box')} itemScope itemType="https://schema.org/FAQPage">
            {faqContent.map((f, i) => (
                <div key={i} className={bDog('faqs-listings-box_item')} itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                    <h3 className="snif-m2 snif-semibold mt-3 mb-1" itemProp="name">
                        {f.question}
                    </h3>
                    <div className="snif-p" itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                        {customFaqs ? <div itemProp="text" dangerouslySetInnerHTML={{ __html: f.answer }} /> : <p itemProp="text">{f.answer}</p>}
                    </div>
                </div>
            ))}
        </div>
    </>
);

const RenderMainContent = ({
    activeReset,
    blogPost,
    canShowBreeds,
    changeBreed,
    changeGender,
    changeState,
    defBreed,
    defGender,
    defState,
    emptyTable,
    customFaqs,
    faqContent,
    faqTitle,
    FiltersComp,
    breedTitle,
    genderTitle,
    handleApplyFilters,
    handleResetFilters,
    mainTitle,
    stateTitle,
    showAppBanner,
    SortByComp,
    shareModal,
    toggleShareModal,
    selBreed,
    selGender,
    selState,
    serverSSR,
    TOP_BREEDS,
    useStateSSR,
    genderModal,
    toggleGenderModal,
    breedModal,
    toggleBreedModal,
    stateModal,
    toggleStateModal,
    allDogNames,
    loadMoreDogNames,
    allLastPage,
}) => {
    return (
        <div className="mx-2 mx-md-0">
            <h1 className="snif-m1 snif-xl-pc snif-semibold snif-bold-pc text-capitalize mb-3 mb-md-4">{mainTitle}</h1>
            <BlogSubDetail {...{ blogPost, updatedAt, useStateSSR, type: BLOG_TYPES.DOG_NAMES.id }} />
            <div className="mb-4">
                {!emptyTable && (
                    <p className={bDog('p-lora')}>
                        <RenderMainAbout {...{ blogPost, defBreed, defGender, defState }} />
                    </p>
                )}
                <div>
                    <div className={bDog('filters').mix(showAppBanner ? 'has-app-banner' : '')}>
                        <div>{FiltersComp}</div>
                        <div>
                            <div className="d-none d-md-block" />
                            <SniffButton className="d-md-none" size="md" onClick={handleApplyFilters}>
                                Apply filters
                            </SniffButton>
                            <div className={bDog('filters_reset').mix(activeReset ? 'text-deep' : 'disabled')} onClick={handleResetFilters}>
                                Reset filters
                            </div>
                        </div>
                    </div>
                    <div className={bDog('sort')}>
                        <div>
                            <p>Sort:</p>
                            {SortByComp}
                        </div>
                        <SharePage {...{ title: 'Share dog names!', TriggerComp, shareModal, toggleShareModal }} />
                    </div>
                    <div>
                        <DogNameTableMobile
                            {...{
                                loadMoreDogNames,
                                sortedDogNames: allDogNames,
                                lastPage: allLastPage,
                                handleResetFilters,
                                serverSSR,
                            }}
                        />
                        <DogNameTableDesktop
                            {...{
                                loadMoreDogNames,
                                sortedDogNames: allDogNames,
                                lastPage: allLastPage,
                                handleResetFilters,
                                serverSSR,
                            }}
                        />
                    </div>
                </div>
                <Banner1 />
                {!emptyTable && (
                    <>
                        {blogPost?.genders?.length > 0 && (
                            <>
                                <h2 className="snif-m1 snif-l2-pc snif-semibold text-capitalize mb-2">{genderTitle}</h2>
                                <div className={bDog('inter-url-list')}>
                                    {blogPost?.genders?.map((g, i) => (
                                        <a key={i} href={g.slug} rel={g.noFollow ? 'nofollow' : ''} className="text-capitalize">
                                            {g.gender?.toLocaleLowerCase()}
                                        </a>
                                    ))}
                                </div>
                                <div className="py-4">
                                    <SnifHr w100 />
                                </div>
                            </>
                        )}
                        {canShowBreeds && (
                            <>
                                <h2 className="snif-m1 snif-l2-pc snif-semibold text-capitalize mb-2">{breedTitle}</h2>
                                <div className={bDog('inter-url-list')}>
                                    {blogPost.topBreeds?.map((b, i) => {
                                        return (
                                            <a key={i} href={b.slug} rel={b.noFollow ? 'nofollow' : ''}>
                                                {b.breed == 'mix' ? 'Mix' : b.breed}
                                            </a>
                                        );
                                    })}
                                </div>
                                <div className="py-4">
                                    <SnifHr w100 />
                                </div>
                            </>
                        )}
                        {blogPost.states?.length > 0 && (
                            <>
                                <h2 className="snif-m1 snif-l2-pc snif-semibold text-capitalize mb-2">{stateTitle}</h2>
                                <div className={bDog('inter-url-list').mix('state')}>
                                    {blogPost.states?.map((s, i) => {
                                        return (
                                            <a key={i} href={s.slug} rel={s.noFollow ? 'nofollow' : ''}>
                                                {s.name}
                                            </a>
                                        );
                                    })}
                                </div>
                                <div className="py-4">
                                    <SnifHr w100 />
                                </div>
                            </>
                        )}
                        <RenderFaqs {...{ customFaqs, faqTitle, faqContent }} />
                        <Banner2 />
                    </>
                )}
            </div>
            <ModalMobile title="Genders option" isOpen={genderModal} toggle={toggleGenderModal} className={bDog('mob-modal-opt')}>
                <div className={bDog('list-opt')}>
                    {GENDERS.map((g, i) => {
                        const selected = g.value === selGender.value;
                        return (
                            <p key={i} className={selected ? 'selected' : ''} onClick={() => changeGender(i)}>
                                {g.label}
                                {selected && <SvgChevronDown />}
                            </p>
                        );
                    })}
                </div>
            </ModalMobile>
            <ModalMobile title="Breeds option" isOpen={breedModal} toggle={toggleBreedModal} className={bDog('mob-modal-opt')}>
                <div className={bDog('list-opt')}>
                    {TOP_BREEDS.map((b, i) => {
                        const selected = b.value === selBreed.value;
                        return (
                            <p key={i} className={selected ? 'selected' : ''} onClick={() => changeBreed(i)}>
                                {b.label}
                                {selected && <SvgChevronDown />}
                            </p>
                        );
                    })}
                </div>
            </ModalMobile>
            <ModalMobile title="States option" isOpen={stateModal} toggle={toggleStateModal} className={bDog('mob-modal-opt')}>
                <div className={bDog('list-opt')}>
                    {STATES.map((s, i) => {
                        const selected = s.value === selState.value;
                        return (
                            <p key={i} className={selected ? 'selected' : ''} onClick={() => changeState(i)}>
                                {s.label}
                                {selected && <SvgChevronDown />}
                            </p>
                        );
                    })}
                </div>
            </ModalMobile>
            <BlogSubDetail {...{ blogPost, updatedAt, useStateSSR, type: BLOG_TYPES.DOG_NAMES.id }} noMargin />
            <BlogAuthorDetails {...{ author: blogPost?.author, useStateSSR }} />
        </div>
    );
};

const listBread = (defGender, defBreed, defState, dogName) => {
    const list = [
        { name: 'Blog', url: RouteFormatter.blog({}) },
        { name: 'Dog Names', url: RouteFormatter.dogNames() },
    ];

    if (dogName) {
        list.push({ name: dogName });
        return list;
    }

    if (defGender?.slug) {
        list.push({ name: defGender.name, url: RouteFormatter.dogNamesSub({ slug: defGender.slug }) });
    }

    if (defBreed?.slug) {
        const mySlug = defGender?.slug ? `${defGender.slug}-${defBreed.slug}` : defBreed.slug;
        list.push({ name: defBreed.name, url: RouteFormatter.dogNamesSub({ slug: mySlug }) });
    }

    if (defState?.slug) {
        list.push({ name: defState.name });
    }

    return list;
};

const RenderCarouselServer = ({ photos, setMobilePhotoGallery }) => (
    <SniffCarouselSSR
        {...{
            item: { allPhotos: photos },
            useEffect,
            useState,
            showArrow: false,
            server: true,
        }}
        onClick={() => setMobilePhotoGallery(true)}
        spotGallery
    />
);

const RenderCarouselClient = ({ photos, setMobilePhotoGallery }) => (
    <SniffCarouselClient images={photos} type={PREVIEW_TYPES.VIEW} disableArrow onClick={() => setMobilePhotoGallery(true)} />
);

const RenderDesktopPhotoGallery = (props) => <DesktopPhotoGallery {...props} />;

const RenderDogNameProfile = ({
    blogCategories,
    blogPost,
    defBreed,
    defGender,
    defState,
    handleGo,
    isMobile,
    loading,
    loadingCat,
    nextPost,
    previousPost,
    recentPosts,
    showAppBanner,
    updateNamesLoading,
    useStateSSR,
}) => {
    const [Carousel, setCarousel] = useState(() => RenderCarouselServer);
    const [DesktopPhoto, setDesktopPhoto] = useState(() => () => '');
    const [nameAbouts, setNameAbouts] = useState(blogPost?.nameAbouts?.slice(0, 15) || []);
    const [mobilePhotoGallery, setMobilePhotoGallery] = useState(false);
    const [desktopPhotoGallery, setDesktopPhotoGallery] = useState(false);
    const [desktopPhotoCount, setDesktopPhotoCount] = useState(0);
    const canShowMore = blogPost?.nameAbouts?.length > nameAbouts?.length;
    const dogName = capitalizeText(blogPost?.name || '');
    const mainTitle = `${dogName} dog name - meaning, origin, personality and popularity`;
    const photosTitle = `Photos of dogs named ${dogName}`;
    const photosText = `Here are ${blogPost?.photos?.length} of the latest pictures of dogs named ${dogName} from our database of ${numFormatter(
        blogPost?.dogsCount
    )} dogs with the name ${dogName}.`;
    const aboutsTitle = `About dogs named ${dogName}`;
    const aboutsText = `The following are ${blogPost?.nameAbouts?.length} profiles of dogs named ${dogName} from our database of ${numFormatter(
        blogPost?.dogsCount
    )} dogs with the name ${dogName}.`;

    const handleLoadMore = () => setNameAbouts(blogPost?.nameAbouts);

    const toggleDesktopPhotoGallery = (idx = 0) => {
        setDesktopPhotoGallery(!desktopPhotoGallery);
        setDesktopPhotoCount(idx);
    };

    const handleNextDesktopGallery = () => {
        let newIdxPhoto = desktopPhotoCount;

        if (newIdxPhoto + 1 == blogPost?.photos?.length) {
            newIdxPhoto = 0;
        } else {
            newIdxPhoto++;
        }

        setDesktopPhotoCount(newIdxPhoto);
    };

    const handleBackDesktopGallery = () => {
        let newIdxPhoto = desktopPhotoCount;

        if (newIdxPhoto - 1 < 0) {
            newIdxPhoto = blogPost?.photos?.length - 1;
        } else {
            newIdxPhoto--;
        }

        setDesktopPhotoCount(newIdxPhoto);
    };

    const CarouselComp = <Carousel {...{ photos: blogPost?.photos, setMobilePhotoGallery }} />;
    const DesktopPhotoGalleryComp = (
        <DesktopPhoto
            photos={blogPost?.photos?.map((p) => ({ imageAws: p }))}
            currentPhoto={desktopPhotoCount}
            isOpen={desktopPhotoGallery}
            toggle={toggleDesktopPhotoGallery}
            handleNext={handleNextDesktopGallery}
            handleBack={handleBackDesktopGallery}
            setDesktopPhotoCount={setDesktopPhotoCount}
            useState={useState}
            guestReq={false}
        />
    );

    useEffect(() => {
        const limited = blogPost?.nameAbouts?.slice(0, 15);
        setNameAbouts(limited);
    }, [blogPost?.nameAbouts]);

    useEffect(() => {
        setCarousel(() => RenderCarouselClient);
        setDesktopPhoto(() => RenderDesktopPhotoGallery);
    }, []);

    return (
        <>
            {!useStateSSR && (
                <Helmet>
                    <title>{blogPost?.meta?.title}</title>
                    <meta name="description" content={blogPost?.meta?.description} />
                </Helmet>
            )}
            <div className={`${b()} ${bDog()}`}>
                <BlogTopSticky {...{ showAppBanner }} />
                <BlogTopNav />
                <div className="d-none d-md-block b-bot mb-3" />
                <div className="desktop-container">
                    <div className="mx-2 mx-md-0">
                        <SpotBreadcrumbs
                            {...{
                                listBread: listBread(defGender, defBreed, defState, dogName),
                                SSR: !!useStateSSR,
                                general: true,
                            }}
                        />
                    </div>
                </div>
                <div className={b('content').mix('desktop-container')}>
                    {loading || updateNamesLoading || loadingCat ? (
                        <Preloader full />
                    ) : (
                        <div className="mx-2 mx-md-0">
                            <h1 className="snif-m1 snif-xl-pc snif-semibold snif-bold-pc mb-3">{mainTitle}</h1>
                            <p className={bDog('p-lora')}>{blogPost?.about}</p>
                            <h2 className="snif-m1 snif-semibold mt-4 mb-1">Popularity of the name</h2>
                            <ul className={bDog('p-lora').mix('dg-profile_popular')}>
                                {blogPost?.rank && (
                                    <li className="text-primary">
                                        #{blogPost?.rank} <a href={RouteFormatter.dogNames()}>most popular dog name</a>
                                    </li>
                                )}
                                {blogPost?.popularGender?.slug && (
                                    <li>
                                        Most popular gender:{' '}
                                        <a href={blogPost?.popularGender?.slug}>{capitalizeText(blogPost?.popularGender?.name)}</a>
                                    </li>
                                )}
                                {blogPost?.popularBreed?.slug && (
                                    <li>
                                        Most popular breed: <a href={blogPost?.popularBreed?.slug}>{blogPost?.popularBreed?.name}</a>
                                    </li>
                                )}
                            </ul>
                            {blogPost?.photos?.length > 0 && (
                                <>
                                    <h2 className="snif-m1 snif-semibold mt-4 mb-1">{photosTitle}</h2>
                                    <p className={bDog('p-lora')}>{photosText}</p>
                                    <div className="dg-profile_gallery">
                                        <div className="d-md-none">{CarouselComp}</div>
                                        {blogPost?.photos?.length == 1 ? (
                                            <div className="dg-profile_gallery_desktop1">
                                                <PictureSet
                                                    imgs={ImageHelper.getUrlFromOptions(blogPost?.photos[0], `full-fit-in/${IMG_RESIZES.x768}`)}
                                                    width={768}
                                                    height={512}
                                                    alt={`${photosTitle} #1`}
                                                    className="c-pointer"
                                                    onClick={() => toggleDesktopPhotoGallery(0)}
                                                />
                                            </div>
                                        ) : blogPost?.photos?.length == 2 ? (
                                            <div className="dg-profile_gallery_desktop2">
                                                <div>
                                                    <PictureSet
                                                        imgs={ImageHelper.getUrlFromOptions(blogPost?.photos[0], `full-fit-in/${IMG_RESIZES.x768}`)}
                                                        width={768}
                                                        height={512}
                                                        alt={`${photosTitle} #1`}
                                                        className="c-pointer"
                                                        onClick={() => toggleDesktopPhotoGallery(0)}
                                                    />
                                                </div>
                                                <div>
                                                    <PictureSet
                                                        imgs={ImageHelper.getUrlFromOptions(blogPost?.photos[1], `full-fit-in/${IMG_RESIZES.x768}`)}
                                                        width={768}
                                                        height={512}
                                                        alt={`${photosTitle} #2`}
                                                        className="c-pointer"
                                                        onClick={() => toggleDesktopPhotoGallery(0)}
                                                    />
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="dg-profile_gallery_desktop">
                                                <div>
                                                    <PictureSet
                                                        imgs={ImageHelper.getUrlFromOptions(
                                                            blogPost?.photos[0] || emptyImg,
                                                            `full-fit-in/${IMG_RESIZES.x768}`
                                                        )}
                                                        width={768}
                                                        height={512}
                                                        alt={`${photosTitle} #1`}
                                                        className="c-pointer"
                                                        onClick={() => toggleDesktopPhotoGallery(0)}
                                                    />
                                                </div>
                                                <div>
                                                    <PictureSet
                                                        imgs={ImageHelper.getUrlFromOptions(
                                                            blogPost?.photos[1] || emptyImg,
                                                            `full-fit-in/${IMG_RESIZES.x768}`
                                                        )}
                                                        width={768}
                                                        height={512}
                                                        alt={`${photosTitle} #2`}
                                                        className="c-pointer"
                                                        onClick={() => toggleDesktopPhotoGallery(0)}
                                                    />
                                                    <PictureSet
                                                        imgs={ImageHelper.getUrlFromOptions(
                                                            blogPost?.photos[2] || emptyImg,
                                                            `full-fit-in/${IMG_RESIZES.x768}`
                                                        )}
                                                        width={768}
                                                        height={512}
                                                        alt={`${photosTitle} #3`}
                                                        className="c-pointer"
                                                        onClick={() => toggleDesktopPhotoGallery(0)}
                                                    />
                                                </div>
                                                <SniffButton
                                                    className="dg-profile_gallery_desktop_cta"
                                                    color="secondary"
                                                    onClick={() => toggleDesktopPhotoGallery(0)}
                                                >
                                                    Show all photos
                                                </SniffButton>
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                            {blogPost?.nameAbouts?.length > 0 && (
                                <>
                                    <h2 className="snif-m1 snif-semibold mt-4 mb-1">{aboutsTitle}</h2>
                                    <p className={bDog('p-lora')}>{aboutsText}</p>
                                    <ul className="dg-profile_abouts">
                                        {nameAbouts?.map((a, i) => (
                                            <li key={i}>
                                                <p className={bDog('p-lora')}>“{a.about}“</p>
                                                <p className="snif-p snif-semibold">— {a.ownerName}</p>
                                            </li>
                                        ))}
                                        {canShowMore && (
                                            <SniffButton size="lg" color="secondary" onClick={handleLoadMore}>
                                                Load more
                                            </SniffButton>
                                        )}
                                    </ul>
                                </>
                            )}
                            <div className="py-4">
                                <SnifHr w100 />
                            </div>
                            <div className="mb-4">
                                <h2 className="snif-m1 snif-l2-pc snif-semibold text-capitalize mb-2">Most Popular Dog Names By Gender</h2>
                                <div className={bDog('inter-url-list')}>
                                    {blogPost?.genders?.map((g, i) => (
                                        <a key={i} href={g.slug} rel={g.noFollow ? 'nofollow' : ''} className="text-capitalize">
                                            {g.name.toLocaleLowerCase()}
                                        </a>
                                    ))}
                                </div>
                                <div className="py-4">
                                    <SnifHr w100 />
                                </div>
                                <h2 className="snif-m1 snif-l2-pc snif-semibold text-capitalize mb-2">Most Popular Dog Names By Breed</h2>
                                <div className={bDog('inter-url-list')}>
                                    {blogPost.topBreeds?.map((b, i) => {
                                        return (
                                            <a key={i} href={b.slug} rel={b.noFollow ? 'nofollow' : ''}>
                                                {b.name}
                                            </a>
                                        );
                                    })}
                                </div>
                                <div className="py-4">
                                    <SnifHr w100 />
                                </div>
                                <h2 className="snif-m1 snif-l2-pc snif-semibold text-capitalize mb-2">Most Popular Dog Names By State</h2>
                                <div className={bDog('inter-url-list').mix('state')}>
                                    {blogPost.states?.map((s, i) => {
                                        return (
                                            <a key={i} href={s.slug} rel={s.noFollow ? 'nofollow' : ''}>
                                                {s.name}
                                            </a>
                                        );
                                    })}
                                </div>
                                <Banner2 />
                            </div>
                            <BlogSubDetail {...{ blogPost, updatedAt, useStateSSR, type: BLOG_TYPES.DOG_NAMES.id }} noMargin />
                            <BlogAuthorDetails {...{ author: blogPost?.author, useStateSSR }} />
                        </div>
                    )}
                    <BlogSideBar {...{ blogPost, useStateSSR }} />
                </div>
                <RenderSeoContent {...{ blogPost, blogCategories, handleGo, previousPost, nextPost, recentPosts, useStateSSR }} />
                <BlogFooter {...{ isMobile, useStateSSR }} />
            </div>
            {!useStateSSR && <FooterNew isShowCities={false} />}
            <MobileSubPage title={`Photos of dogs named ${dogName}`} show={mobilePhotoGallery} onBack={() => setMobilePhotoGallery(false)}>
                <>
                    {blogPost?.photos?.map((p, i) => (
                        <PictureSet
                            key={i}
                            imgs={ImageHelper.getUrlFromOptions(p, `full-fit-in/${IMG_RESIZES.x768}`)}
                            width={768}
                            height={512}
                            alt={`${photosTitle} #${i + 1}`}
                            className="h-auto mb-2"
                            style={{
                                width: 'calc(100% + 32px)',
                                marginLeft: '-16px',
                                marginRight: '-16px',
                                objectFit: 'contain',
                                objectPostion: 'center',
                            }}
                        />
                    ))}
                </>
            </MobileSubPage>
            {DesktopPhotoGalleryComp}
        </>
    );
};

const updatedAt = 'Aug 7, 2026';

const DogNames = ({ dataSSR, categoriesSSR, useStateSSR, serverSSR }) => {
    const history = useStateSSR ? () => {} : useHistory.default();
    const isMobile = useMobileScreen();
    const urlPath = useStateSSR ? dataSSR?.slug : window.location.pathname;
    const dogPath = urlPath?.split('/')[2] || null;
    const isProfilePage = typeof dogPath == 'string' ? dogPath.endsWith('-dog-name') : false;
    const { getDogNames } = serverSSR ? {} : isProfilePage ? {} : useGetDogNamesC.default();
    const { data: clientData, loading } = useStateSSR
        ? {}
        : isProfilePage
          ? useGetDogNamesProfile.default(dogPath)
          : useGetDogNamesPage.default(dogPath);
    const { data: clientCat, loading: loadingCat } = useStateSSR ? {} : useGetBlogCategories.default();
    const blogPost = dataSSR || clientData || {};
    const blogCategories = categoriesSSR || clientCat || [];
    const TOP_BREEDS = BREEDS(blogPost?.allTopBreeds);
    const defGender = blogPost?.chosenFilters?.gender?.slug ? blogPost?.chosenFilters?.gender : '';
    const defBreed = blogPost?.chosenFilters?.breed?.slug ? blogPost?.chosenFilters?.breed : '';
    const defState = blogPost?.chosenFilters?.state?.slug ? blogPost?.chosenFilters?.state : '';
    const chosenGender = defGender ? GENDERS.find((g) => g.value === defGender.slug) || GENDERS[0] : GENDERS[0];
    const chosenBreed = defBreed ? TOP_BREEDS.find((b) => b.value === defBreed.slug) || TOP_BREEDS[0] : TOP_BREEDS[0];
    const chosenState = defState ? STATES.find((s) => s.value === defState.slug) || STATES[0] : STATES[0];
    const activeReset = chosenBreed.value != TOP_BREEDS[0].value || chosenGender.value != GENDERS[0].value || chosenState.value != STATES[0].value;
    const [Filters, setFilters] = useState(() => RenderMobileFilters);
    const [SortBy, setSortBy] = useState(() => () => '');
    const [showAppBanner, setShowAppBanner] = useState(null);
    const [selGender, setSelGender] = useState(chosenGender);
    const [selBreed, setSelBreed] = useState(chosenBreed);
    const [selState, setSelState] = useState(chosenState);
    const [firstLoad, setFirstLoad] = useState(true);
    const [selSortBy, setSelSortBy] = useState(SORT_BY[0]);
    const [genderModal, setGenderModal] = useState(false);
    const [breedModal, setBreedModal] = useState(false);
    const [stateModal, setStateModal] = useState(false);
    const [shareModal, setShareModal] = useState(false);

    const [allDogNames, setAllDogNames] = useState(blogPost?.dogNames?.collection || []);
    const [allNamesPage, setAllNamesPage] = useState(1);
    const [updateNamesLoading, setUpdateNamesLoading] = useState(false);

    const allLastPage = allDogNames?.length == 0 || allNamesPage == blogPost?.dogNames?.metadata?.totalPages;

    const recentPosts = blogPost?.recentPosts || [];
    const previousPost = blogPost?.previousPost;
    const nextPost = blogPost?.nextPost;
    const customFaqs = blogPost?.faqs?.length > 0;
    const faqContent = customFaqs ? blogPost.faqs : DEFAULT_FAQS;
    const emptyTable = blogPost?.dogNames?.length == 0;

    const canShowBreeds =
        blogPost?.topBreeds?.length > 0 && (!defBreed?.slug || (defGender?.slug && defBreed?.slug) || (defState?.slug && defBreed?.slug));

    const { breedTitle, faqTitle, genderTitle, mainTitle, stateTitle } = generateTitles({
        defGender,
        defBreed,
        defState,
        chosenGender,
        chosenBreed,
        chosenState,
    });

    const toggleGenderModal = () => setGenderModal((b) => !b);
    const toggleBreedModal = () => setBreedModal((b) => !b);
    const toggleStateModal = () => setStateModal((b) => !b);
    const toggleShareModal = () => setShareModal((b) => !b);

    const loadMoreDogNames = async () => {
        let sortOpt;

        switch (selSortBy.value) {
            case SORT_BY[1].value:
                sortOpt = { sortBy: 'DOGS_COUNT', sortOrder: 'ASC' };
                break;
            case SORT_BY[2].value:
                sortOpt = { sortBy: 'NAME', sortOrder: 'ASC' };
                break;
            case SORT_BY[3].value:
                sortOpt = { sortBy: 'NAME', sortOrder: 'DESC' };
                break;
            default:
                sortOpt = { sortBy: 'DOGS_COUNT', sortOrder: 'DESC' };
                break;
        }

        try {
            const { sortBy, sortOrder } = sortOpt;
            const newPage = allNamesPage + 1;
            const res = await getDogNames({ variables: { slug: dogPath, page: newPage, limit: NAME_PER_PAGE, sortBy, sortOrder } });
            const newDogNames = res?.data?.dogNames || [];
            setAllDogNames((n) => [...n, ...newDogNames]);
            setAllNamesPage(newPage);
        } catch (error) {
            console.warn(error);
        }
    };

    const updateDogNames = async ({ sortBy, sortOrder }) => {
        try {
            const limit = NAME_PER_PAGE;
            const res = await getDogNames({ variables: { slug: dogPath, page: 1, limit, sortBy, sortOrder } });
            setAllDogNames(res.data?.dogNames);
            return true;
        } catch (error) {
            console.warn(error);
            return false;
        }
    };

    const handleResetFilters = () => {
        if (!activeReset) {
            return;
        }
        if (useStateSSR) {
            window.location = RouteFormatter.dogNames();
        } else {
            setSelGender(GENDERS[0]);
            setSelBreed(TOP_BREEDS[0]);
            setSelState(STATES[0]);
            history.push(RouteFormatter.dogNames());
        }
    };

    const handleApplyFilters = () => {
        const mySlug = generateSlug(selGender, selBreed, selState);
        if (useStateSSR) {
            window.location = mySlug ? RouteFormatter.dogNamesSub({ slug: mySlug }) : RouteFormatter.dogNames();
        } else {
            history.push(mySlug ? RouteFormatter.dogNamesSub({ slug: mySlug }) : RouteFormatter.dogNames());
        }
    };

    const changeGender = (i) => {
        setSelGender(GENDERS[i]);
        toggleGenderModal();
    };

    const changeBreed = (i) => {
        setSelBreed(TOP_BREEDS[i]);
        toggleBreedModal();
    };

    const changeState = (i) => {
        setSelState(STATES[i]);
        toggleStateModal();
    };

    const handleGo = (e, slug) => {
        e.preventDefault();
        e.stopPropagation();
        window.location = slug;
    };

    const FiltersComp = (
        <Filters
            {...{
                handleApplyFilters,
                GENDERS,
                selGender,
                setSelGender,
                selBreed,
                setSelBreed,
                selState,
                setSelState,
                TOP_BREEDS,
                toggleBreedModal,
                toggleGenderModal,
                toggleStateModal,
            }}
        />
    );

    const SortByComp = <SortBy {...{ selSortBy, setSelSortBy }} />;

    useEffect(() => {
        setFirstLoad(false);

        if (firstLoad) {
            return;
        }

        const updateNames = async () => {
            try {
                let sortBy = 'DOGS_COUNT';
                let sortOrder = 'DESC';
                if (selSortBy.value == SORT_BY[1].value) {
                    sortBy = 'DOGS_COUNT';
                    sortOrder = 'ASC';
                } else if (selSortBy.value == SORT_BY[2].value) {
                    sortBy = 'NAME';
                    sortOrder = 'ASC';
                } else if (selSortBy.value == SORT_BY[3].value) {
                    sortBy = 'NAME';
                    sortOrder = 'DESC';
                }

                setUpdateNamesLoading(true);

                await updateDogNames({ sortBy, sortOrder });
                setAllNamesPage(1);

                setUpdateNamesLoading(false);
            } catch (error) {
                setUpdateNamesLoading(false);
                console.warn(error);
            }
        };

        updateNames();
    }, [selSortBy]);

    useEffect(() => {
        if (defGender?.slug) {
            setSelGender(chosenGender);
        }
        if (defBreed?.slug) {
            setSelBreed(chosenBreed);
        }
        if (defState?.slug) {
            setSelState(chosenState);
        }
    }, [defGender, defBreed, defState]);

    useEffect(() => {
        if (!clientData) {
            return;
        }

        setAllDogNames(clientData?.dogNames?.collection);
    }, [clientData]);

    useEffect(() => {
        if (!isMobile) {
            setFilters(() => RenderFilters);
        }

        setSortBy(() => RenderSortBy);
    }, []);

    useEffect(() => {
        if (!isMobile) {
            return;
        }

        const hasCookie = CookieService.get(COOKIE_PARAM_NAME.SNIFF_APP_DOWNLOAD_BANNER);
        const canShowSmartBanner = smartBannerShow();

        if (hasCookie || !canShowSmartBanner || !useStateSSR) {
            return;
        } else {
            setShowAppBanner(true);
        }

        const checkCookie = setInterval(() => {
            const hasCookie = CookieService.get(COOKIE_PARAM_NAME.SNIFF_APP_DOWNLOAD_BANNER);
            setShowAppBanner(!hasCookie);
        }, 1000);

        return () => {
            clearInterval(checkCookie);
        };
    }, []);

    if (isProfilePage) {
        return (
            <RenderDogNameProfile
                {...{
                    blogCategories,
                    blogPost,
                    defBreed,
                    defGender,
                    defState,
                    emptyTable,
                    handleGo,
                    isMobile,
                    loading,
                    loadingCat,
                    nextPost,
                    previousPost,
                    recentPosts,
                    showAppBanner,
                    updateNamesLoading,
                    useStateSSR,
                }}
            />
        );
    }

    return (
        <>
            {!useStateSSR && (
                <Helmet>
                    <title>{blogPost?.metaTitle}</title>
                    <meta name="description" content={blogPost?.metaDescription} />
                </Helmet>
            )}
            <div className={`${b()} ${bDog()}`}>
                <BlogTopSticky {...{ showAppBanner }} />
                <BlogTopNav />
                <div className="d-none d-md-block b-bot mb-3" />
                <div className="desktop-container">
                    <div className="mx-2 mx-md-0">
                        <SpotBreadcrumbs
                            {...{
                                listBread: listBread(defGender, defBreed, defState),
                                SSR: !!useStateSSR,
                                general: true,
                            }}
                        />
                    </div>
                </div>
                <div className={b('content').mix('desktop-container')}>
                    {loading || updateNamesLoading || loadingCat ? (
                        <Preloader full />
                    ) : (
                        <RenderMainContent
                            {...{
                                activeReset,
                                blogPost,
                                canShowBreeds,
                                changeBreed,
                                changeGender,
                                changeState,
                                defBreed,
                                defGender,
                                defState,
                                emptyTable,
                                customFaqs,
                                faqContent,
                                faqTitle,
                                FiltersComp,
                                breedTitle,
                                genderTitle,
                                handleApplyFilters,
                                handleResetFilters,
                                mainTitle,
                                stateTitle,
                                showAppBanner,
                                SortByComp,
                                shareModal,
                                toggleShareModal,
                                selBreed,
                                selGender,
                                selState,
                                serverSSR,
                                TOP_BREEDS,
                                useStateSSR,
                                genderModal,
                                toggleGenderModal,
                                breedModal,
                                toggleBreedModal,
                                stateModal,
                                toggleStateModal,
                                allDogNames,
                                loadMoreDogNames,
                                allLastPage,
                            }}
                        />
                    )}
                    <BlogSideBar {...{ blogPost, useStateSSR }} />
                </div>
                <RenderSeoContent {...{ blogPost, blogCategories, handleGo, previousPost, nextPost, recentPosts, useStateSSR }} />
                <BlogFooter {...{ isMobile, useStateSSR }} />
            </div>
            {!useStateSSR && <FooterNew isShowCities={false} />}
        </>
    );
};

export default DogNames;
